import { FC } from 'react';

import { ItiLink as Link } from 'components';
import { useAuth } from 'contexts';
import { ActiveUnit, Booking, Chassis, Container, Genset, RedeliveryTableRow, Role } from 'types';

export const getBooleanLabel = (value?: boolean | null) => (value ? 'Yes' : value === false ? 'No' : null);

export const UnitNumber: FC<{ unit?: ActiveUnit | null } & { [P in Role]?: boolean }> = ({
  unit,
  ...clickableRoles
}) => {
  const { userProfile } = useAuth();

  if (!unit) {
    return null;
  }

  const authorized =
    userProfile.role in clickableRoles &&
    (![Role.ADMIN_CUSTOMER, Role.CUSTOMER, Role.EXTERNAL].includes(userProfile.role) ||
      userProfile.customers?.some(({ id }) => id === unit.customer?.id));

  if (authorized && !unit.flatbed && !unit.ownerChassis) {
    return (
      <Link to={`/units/${unit.id}`} className="overflow">
        {unit.unitNumber}
      </Link>
    );
  } else {
    return <i onClick={e => e.stopPropagation()}>{unit.unitNumber}</i>;
  }
};

export const BookingNumber: FC<{ booking?: Booking | null } & { [P in Role]?: boolean }> = ({
  booking,
  ...clickableRoles
}) => {
  const { userProfile } = useAuth();
  if (!booking) {
    return null;
  } else if (
    userProfile.role in clickableRoles &&
    ((userProfile.role !== Role.ADMIN_CUSTOMER &&
      userProfile.role !== Role.CUSTOMER &&
      userProfile.role !== Role.EXTERNAL) ||
      userProfile.customers?.some(({ id }) => id === booking.customer?.id))
  ) {
    return (
      <Link to={`/bookings/${booking.id}`} className="overflow">
        {booking.bookingNumber}
      </Link>
    );
  } else {
    return <i onClick={e => e.stopPropagation()}>{booking.bookingNumber}</i>;
  }
};

export const RedeliveryNumber: FC<{ redelivery?: RedeliveryTableRow | null } & { [P in Role]?: boolean }> = ({
  redelivery,
  ...clickableRoles
}) => {
  const { userProfile } = useAuth();
  if (!redelivery) {
    return null;
  } else if (
    userProfile.role in clickableRoles &&
    ((userProfile.role !== Role.ADMIN_CUSTOMER &&
      userProfile.role !== Role.CUSTOMER &&
      userProfile.role !== Role.EXTERNAL) ||
      userProfile.customers?.some(({ id }) => id === redelivery.customerId))
  ) {
    return (
      <Link to={`/redeliveries/${redelivery.id}`} className="overflow">
        {redelivery.redeliveryNumber}
      </Link>
    );
  } else {
    return <i onClick={e => e.stopPropagation()}>{redelivery.redeliveryNumber}</i>;
  }
};

export const StackUnitNumber: FC<
  {
    unitNumber?: string | null;
    stack?: ActiveUnit[] | Chassis[] | Container[] | Genset[] | null;
    flatbed?: boolean | null;
    ownerChassis?: boolean | null;
  } & {
    [P in Role]?: boolean;
  }
> = ({ flatbed, ownerChassis, stack, unitNumber, ...clickableRoles }) => {
  const { userProfile } = useAuth();
  const children =
    !stack || stack.length <= 1 ? (
      unitNumber
    ) : (
      <div className="d-flex">
        <div className="flex-shrink-1">{unitNumber}&nbsp;</div>
        <div className="flex-shrink-0">(+{stack.length - 1})</div>
      </div>
    );

  const authorized =
    userProfile.role in clickableRoles &&
    ((userProfile.role !== Role.ADMIN_CUSTOMER &&
      userProfile.role !== Role.CUSTOMER &&
      userProfile.role !== Role.EXTERNAL) ||
      userProfile.customers?.some(({ id }) => stack?.some(({ customer }) => customer?.id === id)));

  if (authorized && !flatbed && !ownerChassis && stack?.length) {
    const primaryUnit =
      stack.find(u => u.unitNumber === unitNumber) ?? stack.find(u => !u.flatbed && !u.ownerChassis) ?? stack[0];
    return (
      <Link to={`/units/${primaryUnit.id}`} className="overflow">
        {children}
      </Link>
    );
  } else {
    return <i onClick={e => e.stopPropagation()}>{children}</i>;
  }
};
